import { useLatest } from '@qfa/react-components';
import ColumnsContext from 'components/Table/contexts/ColumnsContext';
import { orderColumns } from 'components/Table/utils';
import React, { useCallback, useContext, useMemo } from 'react';

import { useColumnControl, useControlRegistration } from '../ColumnControls/hooks';
import { OrderController, OrderControllerId, OrderControlProps } from './types';

const OrderControl: React.FunctionComponent<OrderControlProps> = (props) => {
  const { columnNames, state } = props;
  const { hasControl, controlledColumns, updateColumn } = useColumnControl(columnNames, state);
  const { columns, getColumn } = useContext(ColumnsContext);

  const latestAllColumns = useLatest(columns);
  const latestControlledColumns = useLatest(controlledColumns);

  const set = useCallback(
    (name: string, newIndex) => {
      const movedColumn = getColumn(name);
      if (hasControl(name) && movedColumn) {
        // create new order of all controlled columns
        let newOrder = orderColumns(latestControlledColumns.current)
          // exclude the one we're changing
          .filter(({ name: colName }) => colName !== name);

        // insert the changed column at the new index
        newOrder.splice(newIndex, 0, movedColumn);
        // remove un-orderable columns to get ordered list of order-controlled columns
        newOrder = newOrder.filter(({ name: colName }) => hasControl(colName));

        // un-orderable and uncontrolled columns remain static
        // insert them back at their previous index
        orderColumns(latestAllColumns.current).forEach((staticColumn, index) => {
          if (
            newOrder.findIndex((orderedColumn) => staticColumn.name === orderedColumn.name) === -1
          ) {
            newOrder.splice(index, 0, staticColumn);
          }
        });

        // newOrder now contains all columns, and our moved column in the correct location.
        updateColumn(
          // explicitly order all columns
          ...newOrder.map(({ name: colName }, index) => {
            return { name: colName, order: index };
          })
        );
      }
    },

    [getColumn, hasControl, latestControlledColumns, latestAllColumns, updateColumn]
  );

  const control = useMemo(() => {
    const controller: OrderController = {
      id: OrderControllerId,
      handlers: { set },
      hasControl,
    };
    return controller;
  }, [set, hasControl]);

  useControlRegistration(control);
  const renderOrderControl = () => {
    return null;
  };

  return renderOrderControl();
};

export default OrderControl;
